import { bootstrapPort } from './external-shell-bridge.js';
/**
 * The object a game holds.
 *
 * The design goal is that the correct integration is the shortest one. A game
 * that calls `connect()`, registers its actions, and calls `submitScore()` gets
 * the required action algorithm, contract validation, dedupe, ACK-on-every-path,
 * idempotent retry and reconnection without naming any of them. Everything that
 * could be got wrong by omission has a safe default; everything that cannot be
 * defaulted safely is a required argument.
 *
 * What the game cannot do through this object is deliberate:
 *
 *   - it cannot choose its ranking track. `session.rankingTrack` is readable so
 *     a game can display the board it is playing for, and there is no setter.
 *     The column is owned by a database trigger from the server-side session row
 *     (RANK-004), so even a modified SDK changes nothing.
 *   - it cannot see a provider payload. Actions arrive already reduced to a
 *     game-declared `actionKey` and payload by the rule engine; the raw TikTok or
 *     YouTube event never crosses this boundary (invariant 7).
 *   - it cannot obtain a durable credential. The launch token is short-lived and
 *     session-scoped, and this package never *writes* to `localStorage` or
 *     `document.cookie`. It reads them in exactly one place — the sandbox
 *     self-probe in `bootstrap.ts`, whose entire purpose is to demonstrate that
 *     both are unreachable — and stores nothing anywhere.
 */
import { ActionProcessor, } from './action-processor.js';
import { waitForBootstrap, reportSandboxProbes } from './bootstrap.js';
import { SDK_CHANNEL } from './bridge-protocol.js';
import { BridgeTransport } from './bridge-transport.js';
import { DirectTransport } from './direct-transport.js';
import { TRYPORTALError } from './errors.js';
import { digestOf, newUlid, newUuid } from './ids.js';
import { compilePayloadSchema } from './payload-schema.js';
import { enableStreamMode } from './stream-mode.js';
export const SDK_VERSION = '3.3.5';
/**
 * The largest page a game may ask for.
 *
 * Matches `MAX_LIMIT` in the catalog module, so the SDK refuses a value the API
 * would silently clamp. A clamped request is worse than a rejected one: the game
 * gets fewer rows than it asked for, with nothing saying so, and paginates past
 * entries it never saw.
 */
export const MAX_LEADERBOARD_LIMIT = 100;
export class TRYPORTALGame {
    transport;
    processor;
    logger;
    state = 'IDLE';
    actionCount = 0;
    lastActionId = null;
    closed = false;
    stateListeners = new Set();
    ackListeners = new Set();
    sessionListeners = new Set();
    /** @internal — use `connect()`. */
    constructor(transport, options) {
        this.transport = transport;
        this.logger = options.logger ?? (() => undefined);
        this.processor = new ActionProcessor({
            gameSessionId: transport.session.gameSessionId,
            sdkVersion: SDK_VERSION,
            newAckId: newUlid,
            ...(options.now === undefined ? {} : { now: options.now }),
            ...(options.dedupeCapacity === undefined ? {} : { dedupeCapacity: options.dedupeCapacity }),
            ...(options.clockSkewToleranceMs === undefined
                ? {}
                : { clockSkewToleranceMs: options.clockSkewToleranceMs }),
            ...(options.handlerTimeoutMs === undefined
                ? {}
                : { handlerTimeoutMs: options.handlerTimeoutMs }),
        });
    }
    /** Which session this is, including the track it will be ranked on. */
    get session() {
        return this.transport.session;
    }
    get connectionState() {
        return this.state;
    }
    /** True when this launch is ranked on the live-interactive board. */
    get isLive() {
        return this.transport.session.rankingTrack === 'LIVE_INTERACTIVE';
    }
    /** Whether this game is talking through the parent frame or directly. */
    get transportKind() {
        return this.transport.kind;
    }
    get appliedActionCount() {
        return this.actionCount;
    }
    /**
     * Which action keys currently have a handler.
     *
     * `defineActions().register()` reads this to tell "declared and forgotten"
     * apart from "declared and registered somewhere else" — `enableStreamMode()`
     * registers the three `tp_live_*` actions itself, and demanding a second
     * handler for them would be wrong.
     */
    get registeredActionKeys() {
        return this.processor.registeredKeys;
    }
    /** @internal */
    async start() {
        await this.transport.start({
            onAction: async (envelope) => {
                const ack = await this.processor.process(envelope);
                if (ack.status === 'APPLIED') {
                    this.actionCount += 1;
                    this.lastActionId = envelope.actionId;
                }
                for (const listener of this.ackListeners) {
                    try {
                        listener(ack);
                    }
                    catch (error) {
                        this.logger('an onActionAck listener threw', error);
                    }
                }
                return ack;
            },
            onSessionState: (state, killSwitchActive, reason) => {
                // A kill switch is not a hint. Consumption stops immediately and queued
                // work is refused rather than held (LIVE-008, LIVE-009).
                if (killSwitchActive || state === 'PAUSED') {
                    this.processor.pause(reason);
                }
                else if (state === 'ACTIVE') {
                    this.processor.resume();
                }
                for (const listener of this.sessionListeners) {
                    try {
                        listener({ state, killSwitchActive, reason });
                    }
                    catch (error) {
                        this.logger('an onSessionStateChange listener threw', error);
                    }
                }
            },
            onStateChange: (next) => {
                if (this.state === next)
                    return;
                this.state = next;
                for (const listener of this.stateListeners) {
                    try {
                        listener(next);
                    }
                    catch (error) {
                        this.logger('an onConnectionStateChange listener threw', error);
                    }
                }
            },
            log: this.logger,
        });
    }
    /**
     * Register a handler for one live action key.
     *
     * Returns an unregister function. Registering the same key twice throws
     * rather than replacing, because a silently replaced handler is a bug that
     * only shows up when the action fires.
     */
    onAction(actionKey, registration) {
        const validate = typeof registration.validate === 'function'
            ? registration.validate
            : compilePayloadSchema(registration.validate);
        const action = {
            actionKey,
            validatePayload: validate,
            handle: (payload, envelope, context) => registration.handle(payload, {
                envelope,
                now: context.now,
                isReplayAttempt: context.isReplayAttempt,
                abortSignal: context.abortSignal,
            }),
        };
        return this.processor.register(action);
    }
    /** A game-owned veto, e.g. "no spawns while the pause menu is open". */
    addGuard(guard) {
        return this.processor.addGuard(guard);
    }
    onActionAck(listener) {
        this.ackListeners.add(listener);
        return () => this.ackListeners.delete(listener);
    }
    onConnectionStateChange(listener) {
        this.stateListeners.add(listener);
        return () => this.stateListeners.delete(listener);
    }
    onSessionStateChange(listener) {
        this.sessionListeners.add(listener);
        return () => this.sessionListeners.delete(listener);
    }
    pauseActionConsumption(reason) {
        this.processor.pause(reason);
    }
    resumeActionConsumption() {
        this.processor.resume();
    }
    /**
     * Submit a score and wait for the platform's receipt.
     *
     * Scores never travel over the realtime channel: that socket carries actions,
     * and a lost socket must not be able to lose a score.
     */
    async submitScore(input) {
        if (this.closed) {
            throw new TRYPORTALError('SDK_CLOSED', 'submitScore() was called after close()');
        }
        if (!Number.isSafeInteger(input.score)) {
            throw new TRYPORTALError('INVALID_USAGE', `score must be a safe integer, received ${String(input.score)}. ` +
                "If your game tracks a fractional score, multiply by the manifest's " +
                'score.integerScale and submit the integer.');
        }
        if (input.durationMs !== undefined && !Number.isSafeInteger(input.durationMs)) {
            throw new TRYPORTALError('INVALID_USAGE', 'durationMs must be an integer number of ms');
        }
        if (input.stateDigest !== undefined && !/^[a-f0-9]{64}$/.test(input.stateDigest)) {
            throw new TRYPORTALError('INVALID_USAGE', 'stateDigest must be 64 lowercase hex characters (a SHA-256 digest)');
        }
        const clientSubmissionId = input.clientSubmissionId ?? newUuid();
        const stateDigest = input.stateDigest ??
            (await digestOf([
                this.transport.session.gameSessionId,
                String(input.score),
                String(input.final),
                String(input.durationMs ?? ''),
                String(this.actionCount),
                clientSubmissionId,
            ].join('|')));
        return this.transport.submitScore({
            schemaVersion: 1,
            gameSessionId: this.transport.session.gameSessionId,
            clientSubmissionId,
            scoreValue: input.score,
            final: input.final,
            ...(input.durationMs === undefined ? {} : { durationMs: input.durationMs }),
            proof: {
                sessionNonce: this.transport.sessionNonce,
                clientSequence: this.actionCount,
                stateDigest,
                sdkVersion: SDK_VERSION,
                actionCount: this.actionCount,
                lastActionId: this.lastActionId,
                ...(input.custom === undefined ? {} : { custom: input.custom }),
            },
        });
    }
    /**
     * Read a page of the leaderboard this session is being ranked on.
     *
     * ```js
     * const board = await tryportal.leaderboard();          // top 20
     * const next  = await tryportal.leaderboard({ cursor: board.nextCursor });
     * board.myEntry?.rank;                                  // or null
     * ```
     *
     * Three properties are worth knowing before writing a screen against this.
     *
     * There is no board to choose. The competition comes from the server-owned
     * launch context, so `leaderboard()` shows the STANDARD board for a standard
     * launch and the LIVE_INTERACTIVE board for a live one, and no argument can
     * change that (CLAUDE.md invariants 1 and 2, RANK-005/006/007).
     *
     * `myEntry` is `null`, not an error, when the player has no public row. That
     * is the normal state for a guest: invariant 4 keeps a guest's verified score
     * off every public board until the same user id gains a passkey. Draw the
     * board and invite the upgrade — do not report a failure, and do not
     * fabricate a row.
     *
     * The call is throttled by the shell, not by the game. Asking once per frame
     * is answered from a short-lived cache and, past the allowance, rejected with
     * a retryable `LEADERBOARD_UNAVAILABLE`. Read it when the screen that shows
     * it opens and after a score is accepted; a board does not need a frame rate.
     */
    async leaderboard(query = {}) {
        if (this.closed) {
            throw new TRYPORTALError('SDK_CLOSED', 'leaderboard() was called after close()');
        }
        if (query.limit !== undefined && (!Number.isSafeInteger(query.limit) || query.limit < 1)) {
            throw new TRYPORTALError('INVALID_USAGE', 'limit must be a positive integer');
        }
        if (query.limit !== undefined && query.limit > MAX_LEADERBOARD_LIMIT) {
            throw new TRYPORTALError('INVALID_USAGE', `limit must be at most ${String(MAX_LEADERBOARD_LIMIT)}; ` +
                'page through the board with cursor instead of asking for all of it');
        }
        if (query.cursor !== undefined && typeof query.cursor !== 'string') {
            throw new TRYPORTALError('INVALID_USAGE', 'cursor must be the nextCursor of a previous page');
        }
        return this.transport.leaderboard(query);
    }
    /** Create a shareable card for a score the platform already accepted. */
    createShareCard(input) {
        if (this.closed) {
            throw new TRYPORTALError('SDK_CLOSED', 'createShareCard() was called after close()');
        }
        return this.transport.createShareCard(input);
    }
    /** End the run. Safe to call more than once. */
    /**
     * Turn on 配信モード: viewer comments and gifts, drawn over the game.
     *
     * ```js
     * const game = await connect();
     * game.enableStreamMode();
     * ```
     *
     * That is the whole integration, provided the game's manifest declares the
     * standard actions — `standardLiveActions()` from `@tryportal/game-manifest`
     * produces the fragment to paste in. Nothing is created and no handler is
     * registered until this is called, and `destroy()` on the result puts the
     * document back exactly as it was.
     *
     * The defaults are deliberately quiet: bottom-left, four rows, 82% opacity,
     * each row gone six seconds after it arrives. Pass `position`, `opacity`,
     * `maxItems`, `fadeAfterMs`, `scale` or `zIndex` to change that, or
     * `render: false` to get the parsed events without any DOM and draw them
     * inside the game's own canvas.
     */
    enableStreamMode(options = {}) {
        return enableStreamMode(this, options);
    }
    /**
     * Ask the play shell to turn 配信モード on or off.
     *
     * The game draws the button. The shell draws the guide (passkey, TikTok
     * handle, confirmation) and restarts the iframe on a LIVE launch. A game
     * running outside the shell cannot do this.
     */
    requestStreamMode(desired) {
        if (this.closed) {
            throw new TRYPORTALError('SDK_CLOSED', 'requestStreamMode() was called after close()');
        }
        return this.transport.requestStreamMode(desired);
    }
    /** Ask the play shell to open the overlay-display sheet. */
    openStreamSettings() {
        if (this.closed) {
            throw new TRYPORTALError('SDK_CLOSED', 'openStreamSettings() was called after close()');
        }
        return this.transport.openStreamSettings();
    }
    /**
     * Ask the play shell to open the creator-voice sheet.
     *
     * There is no text argument. The body is typed in the parent frame so a
     * compromised bundle cannot impersonate the creator.
     */
    openCreatorVoice() {
        if (this.closed) {
            throw new TRYPORTALError('SDK_CLOSED', 'openCreatorVoice() was called after close()');
        }
        return this.transport.openCreatorVoice();
    }
    close(reason = 'GAME_COMPLETED') {
        if (this.closed)
            return;
        this.closed = true;
        this.transport.close(reason);
    }
}
/**
 * Connect to TRAIDENT.
 *
 * With no arguments this waits for the platform's bootstrap and speaks through
 * the parent frame, which is what a hosted game wants. Pass `launch` to talk to
 * the platform directly.
 */
export async function connect(options = {}) {
    const transport = options.launch === undefined
        ? await bridgeTransportFor(options)
        : new DirectTransport({
            sessionToken: options.launch.sessionToken,
            gameSessionId: options.launch.gameSessionId,
            realtimeUrl: options.launch.realtimeUrl,
            apiBaseUrl: options.launch.apiBaseUrl,
            sdkVersion: SDK_VERSION,
            ...(options.launch.refreshToken === undefined
                ? {}
                : { refreshToken: options.launch.refreshToken }),
            newClientInstanceId: newUuid,
            ...(options.launch.rankingTrack === undefined
                ? {}
                : { rankingTrack: options.launch.rankingTrack }),
            ...(options.launch.competitionId === undefined
                ? {}
                : { competitionId: options.launch.competitionId }),
            ...(options.launch.liveSessionId === undefined
                ? {}
                : { liveSessionId: options.launch.liveSessionId }),
            ...(options.socketFactory === undefined ? {} : { socketFactory: options.socketFactory }),
            ...(options.fetchImpl === undefined ? {} : { fetchImpl: options.fetchImpl }),
            ...(options.maxReconnectAttempts === undefined
                ? {}
                : { maxReconnectAttempts: options.maxReconnectAttempts }),
            ...(options.scoreRetryAttempts === undefined
                ? {}
                : { scoreRetryAttempts: options.scoreRetryAttempts }),
        });
    const game = new TRYPORTALGame(transport, options);
    await game.start();
    return game;
}
async function bridgeTransportFor(options) {
    const win = options.window ?? globalThis;
    const bootstrap = await waitForBootstrap({
        window: win,
        ...(options.bootstrapTimeoutMs === undefined ? {} : { timeoutMs: options.bootstrapTimeoutMs }),
    });
    if (options.reportSandboxProbes !== false && bootstrapPort(bootstrap) === undefined) {
        // Deliberately not awaited. The passkey probe asks the browser for a
        // credential and waits for it to refuse, which takes as long as it takes;
        // blocking `connect()` on a diagnostic would delay every game's first frame
        // for something no game reads.
        void reportSandboxProbes((message, targetOrigin) => {
            win.parent.postMessage(message, targetOrigin);
        }, bootstrap.expectedParentOrigin);
    }
    return new BridgeTransport({
        bootstrap,
        window: win,
        ...(options.requestTimeoutMs === undefined
            ? {}
            : { requestTimeoutMs: options.requestTimeoutMs }),
    });
}
export { SDK_CHANNEL };
